import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Stack, Button, Grid, Paper, Divider, Chip, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  rlApiService,
  getRLPortfolio,
  getAutonomousTradingStatus,
  getCompetitionStatus,
  getCurrentUser,
} from '../api/rlApi';

function IntroPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [autoStatus, setAutoStatus] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [user, setUser] = useState(null);

  const truncate = (str, max = 48) => {
    if (!str) return '—';
    return str.length > max ? `${str.slice(0, max - 1)}…` : str;
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null || Number.isNaN(Number(num))) return '—';
    return `$${Number(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPct = (num) => {
    if (num === undefined || num === null || Number.isNaN(Number(num))) return '—';
    return `${(Number(num) * 100).toFixed(2)}%`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [userRes, modelsRes, portfolioRes, autoRes, competitionRes] = await Promise.allSettled([
          getCurrentUser(),
          rlApiService.listModels(),
          getRLPortfolio(),
          getAutonomousTradingStatus(),
          getCompetitionStatus(),
        ]);

        if (userRes.status === 'fulfilled') {
          setUser(userRes.value?.user || null);
        } else {
          setUser(null);
        }

        if (modelsRes.status === 'fulfilled') {
          setModels(modelsRes.value?.models || []);
        }

        if (portfolioRes.status === 'fulfilled') {
          setPortfolio(portfolioRes.value || null);
        }

        if (autoRes.status === 'fulfilled') {
          setAutoStatus(autoRes.value || null);
        }

        if (competitionRes.status === 'fulfilled') {
          setCompetition(competitionRes.value || null);
        }

        // Collect errors, prefer auth/access-key messaging
        const errors = [modelsRes, portfolioRes, autoRes, competitionRes].filter(
          (r) => r.status === 'rejected'
        );

        if (errors.length) {
          const texts = errors
            .map((r) => r.reason?.message || r.reason?.toString() || '')
            .map((m) => (m || '').toLowerCase());
          const authErr = texts.find(
            (m) => m.includes('401') || m.includes('access key') || m.includes('unauthorized')
          );
          if (authErr) {
            setError("You're not logged in; no portfolio to show. Authenticate and set your access key.");
          } else {
            setError('Unable to load live summaries right now. If you are not logged in or missing an access key, please sign in and set your key.');
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeModel = useMemo(() => {
    return models.find((m) => m.is_deployed) || null;
  }, [models]);

    const summaryCards = useMemo(() => {
      const cards = [];
      if (activeModel) {
        const fullName = activeModel.name;
        cards.push({
          title: 'Active Model',
          value: truncate(fullName, 48),
          fullValue: fullName,
          detail: `Sharpe ${activeModel.performance_metrics?.sharpe_ratio?.toFixed(2) ?? '—'} · Win ${
            activeModel.performance_metrics?.win_rate
              ? `${(activeModel.performance_metrics.win_rate * 100).toFixed(1)}%`
              : '—'
          }`,
          chip: 'Deployed',
          chipColor: 'success',
        });
      }
      if (portfolio) {
        cards.push({
          title: 'Portfolio',
          value: formatCurrency(portfolio.portfolio_value),
          detail: portfolio.total_return_pct !== undefined ? `Total return: ${formatPct(portfolio.total_return_pct)}` : '—',
          chip: autoStatus?.is_running ? 'Autonomous ON' : 'Autonomous OFF',
          chipColor: autoStatus?.is_running ? 'info' : 'default',
        });
      }
      if (competition) {
        cards.push({
          title: 'Competition',
          value: competition.status ? competition.status.toUpperCase() : '—',
          detail: `${competition.active_agents || 0} active / ${competition.total_agents || 0} total`,
          chip: competition.status === 'running' ? 'Live' : 'Idle',
          chipColor: competition.status === 'running' ? 'warning' : 'default',
        });
      }
      return cards;
    }, [activeModel, portfolio, competition, autoStatus]);

    const publicLinks = useMemo(
      () => [
        { label: 'View Demo', to: '/demo', icon: '🎬' },
        { label: 'View Pricing', to: '/pricing', icon: '💲' },
        { label: 'View Roadmap', to: '/roadmap', icon: '🗺️' },
        { label: 'Contact', to: '/contact', icon: '✉️' },
        { label: 'Login', to: '/auth#login', icon: '🔐' },
        { label: 'Register', to: '/auth#register', icon: '📝' },
      ],
      []
    );

    const privateLinks = useMemo(
      () => [
        { label: 'Models', to: '/models', icon: '🤖' },
        { label: 'Training', to: '/training', icon: '🎯' },
        { label: 'Competition', to: '/competition', icon: '🏆' },
        { label: 'Portfolio', to: '/portfolio', icon: '📊' },
        { label: 'View Roadmap', to: '/roadmap', icon: '🗺️' },
      ],
      []
    );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'var(--border)',
          background: 'var(--card)',
          boxShadow: 'var(--glow)',
          color: 'var(--text)',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          🏁 Future AI Alliance – Quant Terminal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }} align="center">
          Welcome to the control center for models, training, competitions, and live portfolios.
          Jump into any area below to manage and monitor your autonomous trading stack.
        </Typography>

        {/* Live summary cards */}
        {loading && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 3,
              background: 'var(--card)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
              borderStyle: 'dashed',
              color: 'var(--text)',
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              Loading live summaries…
            </Typography>
          </Paper>
        )}

        {error && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 3,
              background: 'var(--card)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
              borderStyle: 'dashed',
              color: 'var(--text)',
            }}
          >
            <Typography variant="subtitle1" color="error" gutterBottom>
              {error}
            </Typography>
          </Paper>
        )}

        {!loading && !error && summaryCards.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 3,
              background: 'var(--card)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
              borderStyle: 'dashed',
              color: 'var(--text)',
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              No live summaries available yet.
            </Typography>
          </Paper>
        )}

        {!loading && !error && summaryCards.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    background: 'var(--card)',
                    borderColor: 'var(--border)',
                    boxShadow: 'var(--glow)',
                    color: 'var(--text)',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.title}
                    </Typography>
                    {item.chip && <Chip size="small" label={item.chip} color={item.chipColor} />}
                  </Stack>
                  <Tooltip title={item.fullValue || item.value}>
                    <Typography
                      variant="h6"
                      sx={{
                        lineHeight: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary">
                    {item.detail}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <Grid container spacing={2}>
          {(user ? privateLinks : publicLinks).map((link) => (
            <Grid item xs={12} md={6} key={link.to}>
              <Button
                fullWidth
                size="large"
                variant={user ? 'contained' : 'outlined'}
                component={RouterLink}
                to={link.to}
                sx={{ py: 1.5 }}
                startIcon={<span style={{ fontSize: '1.2rem' }}>{link.icon}</span>}
              >
                {link.label}
              </Button>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={1.2} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" color="text.secondary">
            Quick notes:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Models: deploy, compare, and inspect detailed metrics.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Training: configure standard and evolutionary runs; monitor progress.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Competition: run head-to-head evaluations with leaderboards.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Portfolio: monitor live performance, trades, and autonomous status.
          </Typography>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle1" color="text.secondary">
            Recent activity
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 1.75,
              background: 'var(--card)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
              borderStyle: 'dashed',
              color: 'var(--text)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Activity will be shown here once hooked to live events (deployments, training runs, competitions).
            </Typography>
          </Paper>
        </Stack>
      </Paper>
    </Container>
  );
}

export default IntroPage;

